import React, { useRef, useState } from "react";
import { Upload, FileJson, AlertCircle, ExternalLink, Check, Copy, RefreshCw, X } from "lucide-react";

function CopyBtn({ value, testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-testid={testId}
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))]"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

/**
 * Textarea + file-upload + "Test live URL" widget for the raw .well-known
 * files we serve per-tenant. Validates JSON on the client before save.
 *
 * Used by:
 *   - Tenant admin (`AdminDomain.jsx`) for their own tenant
 *   - Super admin (`SuperAdminPwaBundles.jsx`) for any tenant
 */
export function WellKnownEditor({ label, hint, value, onChange, servedAt, testIdPrefix }) {
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      JSON.parse(text);
      setErr("");
    } catch (ex) {
      setErr(`Invalid JSON in uploaded file: ${ex.message}`);
    }
    onChange(text);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onText = (e) => {
    const v = e.target.value;
    onChange(v);
    if (!v.trim()) { setErr(""); return; }
    try { JSON.parse(v); setErr(""); } catch (ex) { setErr(ex.message); }
  };

  return (
    <div className="border border-[hsl(var(--kb-border))] bg-white p-4" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-sm font-medium flex items-center gap-1.5"><FileJson className="w-4 h-4" />{label}</div>
          {hint && <div className="text-xs text-[hsl(var(--kb-text-muted))] mt-0.5">{hint}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json,.well-known"
            onChange={onPick}
            className="hidden"
            data-testid={`${testIdPrefix}-file-input`}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="px-3 py-1.5 text-xs border border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))] inline-flex items-center gap-1.5"
            data-testid={`${testIdPrefix}-upload-btn`}
          ><Upload className="w-3 h-3" /> Upload file</button>
        </div>
      </div>
      <textarea
        value={value}
        onChange={onText}
        rows={6}
        spellCheck={false}
        placeholder='Paste the raw JSON contents here (or use "Upload file")'
        className="w-full border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] px-3 py-2 text-xs font-mono"
        data-testid={`${testIdPrefix}-textarea`}
      />
      {err && (
        <div className="mt-2 text-xs text-red-700 flex items-center gap-1.5" data-testid={`${testIdPrefix}-error`}>
          <AlertCircle className="w-3 h-3" />Invalid JSON: {err}
        </div>
      )}
      {servedAt && (
        <div className="mt-2 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--kb-text-muted))]">
            <span>Will be served at</span>
            <code className="font-mono break-all">{servedAt}</code>
            <CopyBtn value={servedAt} testId={`${testIdPrefix}-copy`} />
            <a
              href={servedAt}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))]"
              data-testid={`${testIdPrefix}-test-live`}
            ><ExternalLink className="w-3 h-3" /> Open</a>
            <LiveVerifyButton url={servedAt} testIdPrefix={testIdPrefix} />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Fetches the live `.well-known/...` URL with cache-busting and reports
 * exactly what's wrong if verification will fail:
 *   - non-200 → 404 / 500 etc
 *   - HTML body → SPA fallback intercepted, file isn't deployed
 *   - empty JSON array/object → deployed but DB had no entries at build time
 *   - JSON with entries → green check
 */
function LiveVerifyButton({ url, testIdPrefix }) {
  const [state, setState] = useState({ status: "idle", msg: "" });
  const check = async () => {
    setState({ status: "loading", msg: "Checking…" });
    try {
      const bust = `${url}${url.includes("?") ? "&" : "?"}_=${Date.now()}`;
      const res = await fetch(bust, { headers: { Accept: "application/json" } });
      const text = await res.text();
      if (!res.ok) {
        setState({ status: "err", msg: `HTTP ${res.status}` });
        return;
      }
      const looksHtml = /<\!?doctype html|<html/i.test(text.trim().slice(0, 100));
      if (looksHtml) {
        setState({
          status: "err",
          msg: "Returning HTML, not JSON — the file isn't deployed yet. Redeploy the platform to publish.",
        });
        return;
      }
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        setState({ status: "err", msg: "Body isn't valid JSON" });
        return;
      }
      const empty =
        (Array.isArray(parsed) && parsed.length === 0) ||
        (typeof parsed === "object" &&
          parsed !== null &&
          !Array.isArray(parsed) &&
          ((parsed.applinks?.details || []).length === 0));
      if (empty) {
        setState({
          status: "warn",
          msg: "Served as JSON but the file is empty. Your saved entries haven't been baked yet — redeploy the platform to publish.",
        });
        return;
      }
      const count = Array.isArray(parsed)
        ? parsed.length
        : (parsed.applinks?.details || []).length;
      setState({ status: "ok", msg: `Live — ${count} entr${count === 1 ? "y" : "ies"} verified` });
    } catch (e) {
      setState({ status: "err", msg: e.message });
    }
  };
  return (
    <>
      <button
        onClick={check}
        disabled={state.status === "loading"}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))]"
        data-testid={`${testIdPrefix}-verify-btn`}
      >
        {state.status === "loading" ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        Verify live
      </button>
      {state.status !== "idle" && state.status !== "loading" && (
        <span
          className={
            "inline-flex items-center gap-1 px-2 py-1 text-xs " +
            (state.status === "ok"
              ? "text-green-700 border border-green-200 bg-green-50"
              : state.status === "warn"
              ? "text-amber-800 border border-amber-200 bg-amber-50"
              : "text-red-700 border border-red-200 bg-red-50")
          }
          data-testid={`${testIdPrefix}-verify-result`}
        >
          {state.status === "ok" ? <Check className="w-3 h-3" /> : state.status === "warn" ? <AlertCircle className="w-3 h-3" /> : <X className="w-3 h-3" />}
          {state.msg}
        </span>
      )}
    </>
  );
}
